import type { Appointment } from '../../api/timetable'
import { Button } from '../../ui/Control'

/**
 * The sessions of one subject, read back — **the review before an exam**.
 *
 * This is what the whole write-up exists for. Going through a term session by session, each with what
 * came up and what is still to do, is a list somebody can work through; a folder of documents and a
 * text to read again is not. The order is chronological, because a term is followed forwards.
 *
 * **A session nobody wrote about still stands here**, with its date and its title. What was on is a
 * fact about the term whether or not anybody wrote about it, and a review that quietly left out half
 * the term would be a review nobody could trust. It simply says nothing beside it.
 *
 * Two lines and no more of any write-up: a term is thirty sessions, and the whole of one is a tap
 * away at the session itself.
 */
export interface SubjectReviewProps {
  /** The sessions of this subject, oldest first, as the service answers them. */
  readonly appointments: readonly Appointment[]
  readonly onOpen: (id: string) => void
}

export function SubjectReview({ appointments, onOpen }: SubjectReviewProps) {
  const written = appointments.filter((one) => one.writeupLine !== null).length
  const hanging = appointments.reduce((sum, one) => sum + one.links, 0)

  return (
    <section className="subject-detail__part">
      <h3 className="subject-detail__part-title">The term, session by session</h3>
      <p className="subject-detail__aside">
        {written === 0
          ? `${counted(appointments.length, 'session')}, nothing written about them yet. Two lines after a lecture is all it takes.`
          : `${counted(appointments.length, 'session')}, ${String(written)} written about, ${counted(hanging, 'thing')} hanging on them.`}
      </p>

      <ul className="review__list">
        {appointments.map((one) => (
          <li className="review__one" key={one.id}>
            <button
              type="button"
              className="review__hit"
              onClick={() => {
                onOpen(one.id)
              }}
            >
              <span className="review__when">{onDay(one)}</span>
              <span className="review__what">
                <span className="review__lesson">
                  {one.title}
                  {one.cancelled && <span className="review__mark">Cancelled</span>}
                </span>
                {one.writeupLine !== null ? (
                  <span className="review__line">{one.writeupLine}</span>
                ) : (
                  <span className="review__nothing">Nothing written</span>
                )}
              </span>
              {one.links > 0 && (
                <span className="review__hangs">
                  {one.links === 1 ? '1 thing' : `${String(one.links)} things`}
                </span>
              )}
            </button>
            <Button
              tone="bare"
              aria-label={`Open ${one.title}`}
              onClick={() => {
                onOpen(one.id)
              }}
            >
              Open
            </Button>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** The day a session was on, in the zone it was spoken in — never the reader's. */
function onDay(appointment: Appointment): string {
  const at = new Date(appointment.startsAt)
  if (Number.isNaN(at.getTime())) {
    return ''
  }
  return at.toLocaleDateString(undefined, {
    timeZone: appointment.zone,
    day: '2-digit',
    month: '2-digit',
  })
}

function counted(how: number, thing: string): string {
  return how === 1 ? `1 ${thing}` : `${String(how)} ${thing}s`
}
